import React, { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

// Analiz Paneli'nin donut ve çubuk grafikleri (saf SVG — PNG dışa aktarımı için).
//
// Kategorik palet dataviz validator'ından GEÇMİŞ üçlüdür (2026-08-03 koşusu):
//   #0291EF (primary.main) · #01A899 (secondary.dark) · #C77800 (koyu amber)
// #FFC107/#02D3BF ışıklılık bandında FAIL verdi; koyu adımlara "snap" edildi. #C77800
// temada yoktur — grafik verisi paleti bilinçli tek yerde, burada yaşar (tema token'ı olan
// ikisi token'dan okunur). Kontrast WARN'ı (#01A899 2.9:1) doğrudan etiket + lejantla
// karşılanır (validator'ın "relief" koşulu).
const AMBER_DARK = '#C77800';

const DONUT_W = 300;
const DONUT_H = 190;

/** Basit donut: 2-4 dilim, doğrudan etiketli + lejantlı. data: [{label, value}] */
export function HaDonut({ data }) {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const countFmt = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  const palette = [theme.colors.primary.main, theme.colors.secondary.dark, AMBER_DARK];
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total <= 0) return null;

  const cx = 95; const cy = DONUT_H / 2; const r = 70; const ir = 42;
  let acc = 0;
  const slices = data.slice(0, 3).map((d, i) => {
    const a0 = (acc / total) * 2 * Math.PI - Math.PI / 2;
    acc += d.value;
    const a1 = (acc / total) * 2 * Math.PI - Math.PI / 2;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const p = (a, rad) => `${(cx + rad * Math.cos(a)).toFixed(1)},${(cy + rad * Math.sin(a)).toFixed(1)}`;
    return {
      ...d,
      color: palette[i],
      path: `M${p(a0, r)} A${r},${r} 0 ${large} 1 ${p(a1, r)} L${p(a1, ir)} A${ir},${ir} 0 ${large} 0 ${p(a0, ir)} Z`,
      pct: d.value / total,
    };
  });

  return (
    <svg viewBox={`0 0 ${DONUT_W} ${DONUT_H}`} width="100%" height={DONUT_H} role="img">
      {slices.map((s) => (
        <g key={s.label} data-role="donut-slice">
          <title>{`${s.label} — ${countFmt.format(s.value)} (%${Math.round(s.pct * 100)})`}</title>
          {/* dataviz: dilimler arasında 2px yüzey aralığı — stroke kart zemininde */}
          <path d={s.path} fill={s.color} stroke={theme.colors.surface.card} strokeWidth={2} />
        </g>
      ))}
      <text
        x={cx} y={cy + 5} textAnchor="middle" fontSize={20}
        fontFamily={theme.metrics.font.family} fontWeight={800}
        fill={theme.colors.text.primary} style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {countFmt.format(total)}
      </text>
      {/* lejant + doğrudan değer etiketi (kontrast WARN reliefi) — metin TEXT token'ında */}
      {slices.map((s, i) => (
        <g key={`lg-${s.label}`}>
          <circle cx={192} cy={40 + i * 26} r={5} fill={s.color} />
          <text
            x={204} y={44 + i * 26} fontSize={11}
            fontFamily={theme.metrics.font.family} fill={theme.colors.text.primary}
          >
            {`${s.label} · %${Math.round(s.pct * 100)}`}
          </text>
        </g>
      ))}
    </svg>
  );
}

const BAR_W = 320;
const BAR_H = 190;

/** Sıfır etrafında aylık çubuklar (net kadro değişimi). data: [{label, value}] */
export function HaMonthlyBars({ data }) {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const countFmt = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  if (!data || data.length === 0) return null;
  const maxAbs = Math.max(...data.map((d) => Math.abs(d.value)), 1);
  const left = 16; const right = BAR_W - 8; const top = 12; const bottom = BAR_H - 28;
  const zeroY = top + (bottom - top) / 2;
  const scale = (bottom - top) / 2 / maxAbs;
  const step = (right - left) / data.length;
  const barW = Math.max(6, step - 4); // dataviz: bitişik çubuklar arası yüzey aralığı

  return (
    <svg viewBox={`0 0 ${BAR_W} ${BAR_H}`} width="100%" height={BAR_H} role="img">
      <line x1={left} y1={zeroY} x2={right} y2={zeroY} stroke={theme.colors.surface.border} strokeWidth={1} />
      {data.map((d, i) => {
        const h = Math.abs(d.value) * scale;
        const x = left + i * step + (step - barW) / 2;
        const y = d.value >= 0 ? zeroY - h : zeroY;
        return (
          <g key={d.label} data-role="net-bar">
            <title>{`${d.label} — ${d.value > 0 ? '+' : ''}${countFmt.format(d.value)}`}</title>
            {/* Tek seri, tek ton: yön bilgisi konumda (sıfırın altı/üstü) — renk çoğaltılmaz. */}
            <rect
              x={x.toFixed(1)} y={y.toFixed(1)} width={barW.toFixed(1)} height={Math.max(h, 1).toFixed(1)}
              rx={3} fill={theme.colors.primary.main}
              fillOpacity={d.value >= 0 ? 1 : 0.55}
            />
            {i % 3 === 0 && (
              <text
                x={x + barW / 2} y={BAR_H - 10} textAnchor="middle" fontSize={9}
                fontFamily={theme.metrics.font.family} fill={theme.colors.text.muted}
              >
                {d.label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
